import Image from 'next/image'
import { useRouter } from 'next/router'

import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'

import useToggleSdkContext from 'utils/useToogleSdk'
import { useAPIUpdateState } from 'api/kinerja/useAPIKinerja'
import { formatDate, isDateValid } from 'utils/datetime'
import useSKPTrackEvent from '../hooks/useSKPTrackEvent'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  getPelaksanaanDate,
  isDateStarted,
} from 'app/KinerjaShared/utils/pelaksanaan'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'

import type {
  Kinerja,
  KinerjaStatus,
  KinerjaStatusOriginal,
} from 'types/kinerja'
interface CardKinerjaStatusProps {
  skpId: string
  tipe?: Kinerja['tipe']
  status: KinerjaStatus
  onSuccessUpdateSKP: () => any
}

export const getStatusMap = ({
  period,
  isPelaksanaanDatePassed,
  pelaksanaanDate,
  baseUrl,
  periodYear,
  isKinerjaPerencanaan2026Enabled,
  tipe,
}: {
  period: Kinerja['period']
  isPelaksanaanDatePassed: boolean
  pelaksanaanDate: string
  baseUrl: string
  periodYear: number
  isKinerjaPerencanaan2026Enabled: boolean
  tipe?: Kinerja['tipe']
}): Record<
  KinerjaStatusOriginal,
  {
    icon: string
    pretitle: string
    title: string
    description: string | JSX.Element
    ctaText?: string
    ctaButtonColor?: ButtonProps['color']
    ctaHref?: string
  }
> => {
  let pretitle: string, pelaksanaanMonth: string

  if (period === 1) {
    pretitle = '1-31 Januari'
  } else if (period === 2) {
    pretitle = '1-31 Juli'
  }

  if (isDateValid(pelaksanaanDate)) {
    pelaksanaanMonth = formatDate(pelaksanaanDate, 'MMMM yyyy')
  } else {
    pelaksanaanMonth = period === 1 ? 'Februari' : 'Agustus'
  }

  const isGuru = tipe === 'SPF'
  const isNewPerencanaanFlowForGuru =
    isGuru && periodYear >= 2026 && isKinerjaPerencanaan2026Enabled

  return {
    NOT_FOUND: {
      icon: 'img-to-do-list-document.png',
      pretitle,
      title: 'Mulai Perencanaan Kinerja',
      description:
        'Isi Pelaksanaan Tugas Pokok, Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja. Diskusikan juga dengan Kepala Sekolah.',
      ctaText: 'Mulai',
      ctaButtonColor: 'blue',
      ctaHref: `${baseUrl}/konfirmasi-profil`,
    },
    DRAFT: {
      icon: 'img-to-do-list-document.png',
      pretitle,
      title: 'Isi Perencanaan Kinerja',
      description:
        'Isi Pelaksanaan Tugas Pokok, Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja. Diskusikan juga dengan Kepala Sekolah.',
      ctaText: 'Lanjut Rencanakan Kinerja',
      ctaButtonColor: 'blue',
      ctaHref: isNewPerencanaanFlowForGuru
        ? `${baseUrl}/perencanaan/ajukan`
        : `${baseUrl}/perencanaan`,
    },
    WAITING_APPROVAL: {
      icon: 'img-time-document.png',
      pretitle,
      title: 'Menunggu pemeriksaan',
      description:
        'Perencanaan Kinerja Anda sudah diajukan. Anda dapat konfirmasi ke Kepala Sekolah untuk melakukan pemeriksaan.',
      ctaText: 'Cek Perencanaan Kinerja',
      ctaButtonColor: 'white',
      ctaHref: isNewPerencanaanFlowForGuru
        ? `${baseUrl}/perencanaan/rangkuman`
        : `${baseUrl}/perencanaan`,
    },
    APPROVED: {
      icon: 'img-handshake.png',
      pretitle,
      title: 'Sepakati Perencanaan dan kirim ke E-Kinerja',
      description:
        'Perencanaan Anda sudah disetujui oleh Kepala Sekolah. Setelah disepakati, Perencanaan akan otomatis dikirim ke E-Kinerja dalam 1x24 jam.',
      ctaText: 'Sepakati dan Kirim',
      ctaButtonColor: 'blue',
      ctaHref: isNewPerencanaanFlowForGuru
        ? `${baseUrl}/perencanaan/sepakati`
        : `${baseUrl}/perencanaan`,
    },
    REVISED: {
      icon: 'img-time-document.png',
      pretitle,
      title: 'Menunggu pemeriksaan',
      description:
        'Perencanaan Kinerja Anda sudah diajukan. Anda dapat konfirmasi ke Kepala Sekolah untuk melakukan pemeriksaan.',
      ctaText: 'Cek Perencanaan Kinerja',
      ctaButtonColor: 'white',
      ctaHref: isNewPerencanaanFlowForGuru
        ? `${baseUrl}/perencanaan/rangkuman`
        : `${baseUrl}/perencanaan`,
    },
    READY: isPelaksanaanDatePassed
      ? {
          icon: 'img-save-document-drive.png',
          pretitle,
          title: 'Kumpulkan Bukti Dukung Anda',
          description:
            'Bukti Dukung bisa dikumpulkan selama Penilaian Kinerja belum dilakukan oleh atasan.',
          ctaText: 'Lanjut ke Penilaian Kinerja',
          ctaButtonColor: 'blue',
        }
      : {
          icon: 'img-time-document.png',
          pretitle,
          title: 'Menunggu periode Pelaksanaan Kinerja',
          description: (
            <>
              Perencanaan Anda sudah selesai dan disepakati. Anda dapat lanjut
              ke Pelaksanaan mulai dari <strong>{pelaksanaanMonth}</strong>. Cek
              lagi halaman ini nanti, ya.
            </>
          ),
          ctaText: 'Cek Perencanaan Kinerja',
          ctaButtonColor: 'white',
          ctaHref: isNewPerencanaanFlowForGuru
            ? `${baseUrl}/perencanaan/rangkuman`
            : `${baseUrl}/perencanaan`,
        },
    WAITING_EVALUATION: {
      icon: 'img-numeric-mathematical.png',
      pretitle,
      title: 'Menunggu Penilaian Kinerja dari atasan',
      description:
        'Atasan sedang menilai seluruh Hasil Kerja Anda. Nanti nilainya akan muncul di sini. Ditunggu, ya.',
    },
  }
}

export default function CardKinerjaStatus({
  skpId,
  status,
  onSuccessUpdateSKP,
  tipe = 'SPF',
}: CardKinerjaStatusProps) {
  const toggles = useToggleSdkContext()
  const isKinerjaPerencanaan2026Enabled =
    toggles?.['kinerja-perencanaan-2026-enabled'] === true

  const {
    isEnabled: isMultplePeriodEnabled,
    isHavePeriod,
    periods,
    period: selectedPeriod,
    periodYear,
  } = usePeriodContext()
  const isPriorTo2025 = useIsPrior2025()

  const isResolved = Array.isArray(periods) && periods.length > 0
  const period = selectedPeriod.period
  const periodeKinerja = selectedPeriod.periodeKinerja

  const pelaksanaanDate = getPelaksanaanDate(periodeKinerja)
  const isPelaksanaanDatePassed = isResolved && isDateStarted(periodeKinerja)

  const baseUrl = usePengelolaanKinerjaBaseUrl()
  const {
    icon: SelectedIcon,
    pretitle,
    title,
    description,
    ctaText,
    ctaButtonColor,
    ctaHref,
  } = getStatusMap({
    period,
    isPelaksanaanDatePassed,
    pelaksanaanDate,
    baseUrl,
    periodYear,
    isKinerjaPerencanaan2026Enabled,
    tipe,
  })[status]

  const { mutateAsync: updateStateSKP, isLoading: isUpdatingStateSKP } =
    useAPIUpdateState()

  const isReadyForPelaksanaan = status === 'READY' && isPelaksanaanDatePassed
  const isDisabledCta = isReadyForPelaksanaan

  const router = useRouter()
  const { trackEvent } = useSKPTrackEvent()

  const handleCta = async () => {
    if (status === 'NOT_FOUND') {
      trackEvent({
        event_name: 'CLICK_KINERJA_START_PLANNING',
        screen_name: 'KINERJA_HOMEPAGE',
      })
    }

    if (isReadyForPelaksanaan) {
      await updateStateSKP(
        {
          skpId,
        },
        {
          onSuccess() {
            onSuccessUpdateSKP()
          },
        }
      )
    } else {
      router.push(ctaHref)
    }
  }

  return (
    <Card>
      <div className="flex gap-6 p-6">
        <div className="w-[84px] h-[84px] relative">
          {/* Prevent user select */}
          <div className="absolute inset-0 z-10">&nbsp;</div>

          <Image
            alt={title}
            src={['/illustrations', SelectedIcon].join('/')}
            width={84}
            height={84}
          />
        </div>
        <div className="flex-1 ml-6 flex flex-col gap-1 justify-center">
          {isPriorTo2025 && (
            <Text
              variant="subheading"
              color="informational"
              className="!text-blue-60 !uppercase tracking-[0.5px]"
            >
              {pretitle}
            </Text>
          )}
          <Text variant="heading-lg">{title}</Text>
          <Text color="subdued">{description}</Text>
        </div>
        {ctaText && (
          <div className="min-w-[240px]">
            <Button
              size="lg"
              fullWidth
              color={ctaButtonColor}
              disabled={
                isDisabledCta ||
                isUpdatingStateSKP ||
                (isMultplePeriodEnabled && !isHavePeriod)
              }
              isLoading={isUpdatingStateSKP}
              onClick={handleCta}
              data-testid="cta-button"
            >
              {ctaText}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
